import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { userLogin } from '../../services/auth';
import { getCurrentUser } from '../../api/auth';
import safeToast from '../../utils/safeToast';
import { applySeo } from '../../utils/seo';
import { ogBrandImageUrl } from '../../constants/ogBrand';
import type { User } from '../../types';
import CrmSignupModal from './CrmSignupModal';
import { removeBootSplash } from '../../utils/bootSplash';
import FieldTeamLanding from '../fieldteam/FieldTeamLanding';
import FieldTeamSolutionPage from '../fieldteam/FieldTeamSolutionPage';
import { buildFieldTeamJsonLd, FIELD_TEAM_SEO_KEYWORDS } from '../fieldteam/fieldTeamSeo';
import {
  CRM_LOGIN_PATH,
  FIELD_TEAM_PATH,
  SOLUTION_PATHS,
  isFieldTeamSolutionSlug,
  solutionSlugFromPath,
} from '../fieldteam/fieldTeamRoutes';

export default function CrmLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { solution: routeSolution } = useParams<{ solution?: string }>();
  const solution = solutionSlugFromPath(location.pathname)
    || (routeSolution && isFieldTeamSolutionSlug(routeSolution) ? routeSolution : undefined);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [signupOpen, setSignupOpen] = useState(false);
  const [storeLinks, setStoreLinks] = useState<{ playStoreUrl?: string; appStoreUrl?: string }>({});

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('crm_token') || localStorage.getItem('owner_token') || localStorage.getItem('dev_admin_token');
        if (token) {
          const user = await getCurrentUser();
          if (user && user.crmRole) {
            if (!localStorage.getItem('crm_token')) localStorage.setItem('crm_token', token);
            navigate('/crm', { replace: true });
            return;
          }
        }
      } catch { /* ignore */ }
      setChecking(false);
    })();
  }, [navigate]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const signup = String(params.get('signup') || params.get('register') || '').toLowerCase();
      if (['1', 'true', 'yes'].includes(signup)) {
        setSignupOpen(true);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (checking) return;
    removeBootSplash();
  }, [checking]);

  useEffect(() => () => { removeBootSplash(); }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/apk/info');
        if (res.ok) {
          const data = await res.json();
          setStoreLinks({ playStoreUrl: data?.playStoreUrl || '', appStoreUrl: data?.appStoreUrl || '' });
        }
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.tcards.org';
    const path = solution
      ? SOLUTION_PATHS[solution]
      : (location.pathname.replace(/\/+$/, '') || FIELD_TEAM_PATH);
    const title = solution
      ? `${t(`ftl_sol_${solution}_title`)} | TCards`
      : t('ftl_seo_title');
    const description = solution
      ? t(`ftl_sol_${solution}_sub`)
      : t('ftl_seo_desc');
    const image = ogBrandImageUrl(origin, 'crm');
    return applySeo({
      title,
      description,
      keywords: FIELD_TEAM_SEO_KEYWORDS,
      image,
      imageAlt: t('ftl_seo_site_name'),
      url: `${origin}${path}`,
      canonicalUrl: `${origin}${path}`,
      siteName: t('ftl_seo_site_name'),
      jsonLd: buildFieldTeamJsonLd({
        origin,
        path,
        title,
        description,
        image,
        t,
        solutionSlug: solution || null,
      }),
    });
  }, [t, solution, location.pathname]);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      safeToast('error', t('crmLoginEnterCredentials'));
      return;
    }
    setLoading(true);
    try {
      const res = await userLogin(email.trim(), password);
      if (!res.token) {
        safeToast('error', t('crmLoginInvalidCredentials'));
        setLoading(false);
        return;
      }
      localStorage.setItem('crm_token', res.token);
      localStorage.setItem('owner_token', res.token);

      const user: User | null = await getCurrentUser();
      if (!user) {
        safeToast('error', t('crmLoginVerifyFailed'));
        localStorage.removeItem('crm_token');
        localStorage.removeItem('owner_token');
        setLoading(false);
        return;
      }
      if (!user.crmRole || !['client', 'crm_manager', 'sales_rep'].includes(user.crmRole)) {
        safeToast('error', t('crmLoginNoAccess'));
        localStorage.removeItem('crm_token');
        localStorage.removeItem('owner_token');
        setLoading(false);
        return;
      }

      try {
        const access = await import('../../api/crm').then((m) => m.getCrmAccessStatus());
        if (access?.hasCrmRole && access.showCrmUi === false) {
          safeToast('error', t('crmLoginSubInactive'));
          localStorage.removeItem('crm_token');
          localStorage.removeItem('owner_token');
          setLoading(false);
          return;
        }
      } catch { /* non-fatal — allow login if access-status unreachable */ }

      safeToast('success', t('crmLoginWelcomeBackUser', { name: user.name || user.email || '' }));
      setLoading(false);
      navigate('/crm', { replace: true });
    } catch (err: any) {
      safeToast('error', err?.message || t('crmLoginFailed'));
      setLoading(false);
    }
  }

  if (checking) {
    return <div className="min-h-screen bg-[#0b0b22]" aria-busy="true" />;
  }

  if (routeSolution && !isFieldTeamSolutionSlug(routeSolution)) {
    return <Navigate to={CRM_LOGIN_PATH} replace />;
  }

  const auth = {
    email,
    password,
    showPassword,
    loading,
    storeLinks,
    onEmailChange: setEmail,
    onPasswordChange: setPassword,
    onTogglePassword: () => setShowPassword((v) => !v),
    onSubmit: handleLogin,
    signupOpen,
    setSignupOpen,
  };

  return (
    <>
      {solution && isFieldTeamSolutionSlug(solution) ? (
        <FieldTeamSolutionPage slug={solution} {...auth} />
      ) : (
        <FieldTeamLanding {...auth} />
      )}
      <CrmSignupModal
        open={signupOpen}
        onClose={() => setSignupOpen(false)}
        onComplete={() => {
          setSignupOpen(false);
          navigate('/crm', { replace: true });
        }}
      />
    </>
  );
}
